import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContractAnalytics {
  totalContracts: number;
  signedContracts: number;
  signatureRate: number;
  avgTimeToSign: number; // in days
  totalValue: number;
  valueByMonth: Array<{ month: string; value: number; count: number }>;
  statusDistribution: Array<{ status: string; count: number }>;
  recentSignings: Array<{ title: string; signedAt: string; value: number }>;
}

export function useContractAnalytics() {
  return useQuery({
    queryKey: ["contract-analytics"],
    queryFn: async () => {
      const { data: contracts, error } = await supabase
        .from("contracts")
        .select(`
          id, title, status, sent_at, signed_at, created_at,
          deals(amount)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const total = contracts?.length || 0;
      const signed = contracts?.filter(c => c.status === "signed") || [];
      const signedCount = signed.length;
      const signatureRate = total > 0 ? (signedCount / total) * 100 : 0;

      // Calculate average time to sign (in days)
      const signingTimes = signed
        .filter(c => c.sent_at && c.signed_at)
        .map(c => {
          const sent = new Date(c.sent_at!).getTime();
          const signedTime = new Date(c.signed_at!).getTime();
          return (signedTime - sent) / (1000 * 60 * 60 * 24); // days
        });
      const avgTimeToSign = signingTimes.length > 0
        ? signingTimes.reduce((a, b) => a + b, 0) / signingTimes.length
        : 0;

      // Calculate total value
      const totalValue = contracts?.reduce((sum, c) => {
        const deal = c.deals as { amount: number } | null;
        return sum + (deal?.amount || 0);
      }, 0) || 0;

      // Group by month for trends
      const monthMap = new Map<string, { value: number; count: number }>();
      contracts?.forEach(c => {
        const date = new Date(c.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const deal = c.deals as { amount: number } | null;
        const current = monthMap.get(monthKey) || { value: 0, count: 0 };
        monthMap.set(monthKey, {
          value: current.value + (deal?.amount || 0),
          count: current.count + 1
        });
      });

      const valueByMonth = Array.from(monthMap.entries())
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-12); // Last 12 months

      // Status distribution
      const statusMap = new Map<string, number>();
      contracts?.forEach(c => {
        const current = statusMap.get(c.status) || 0;
        statusMap.set(c.status, current + 1);
      });
      const statusDistribution = Array.from(statusMap.entries())
        .map(([status, count]) => ({ status, count }));

      // Recent signings
      const recentSignings = signed
        .slice(0, 5)
        .map(c => {
          const deal = c.deals as { amount: number } | null;
          return {
            title: c.title,
            signedAt: c.signed_at!,
            value: deal?.amount || 0
          };
        });

      return {
        totalContracts: total,
        signedContracts: signedCount,
        signatureRate,
        avgTimeToSign,
        totalValue,
        valueByMonth,
        statusDistribution,
        recentSignings
      } as ContractAnalytics;
    },
  });
}

export interface ContractVersion {
  id: string;
  contract_id: string;
  tenant_id: string;
  version_number: number;
  title: string;
  status: string;
  metadata: Record<string, unknown> | null;
  signer_name: string | null;
  signer_email: string | null;
  expires_at: string | null;
  changed_by: string | null;
  change_summary: string | null;
  created_at: string;
}

export function useContractVersions(contractId: string) {
  return useQuery({
    queryKey: ["contract-versions", contractId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_versions")
        .select("*")
        .eq("contract_id", contractId)
        .order("version_number", { ascending: false });

      if (error) throw error;
      return data as unknown as ContractVersion[];
    },
    enabled: !!contractId,
  });
}

export async function saveContractVersion(
  contractId: string,
  tenantId: string,
  contractData: {
    title: string;
    status: string;
    metadata?: Record<string, unknown> | null;
    signer_name?: string | null;
    signer_email?: string | null;
    expires_at?: string | null;
  },
  changeSummary?: string
) {
  // Get current max version
  const { data: existingVersions } = await supabase
    .from("contract_versions")
    .select("version_number")
    .eq("contract_id", contractId)
    .order("version_number", { ascending: false })
    .limit(1);

  const nextVersion = (existingVersions?.[0]?.version_number || 0) + 1;

  const { data: { user } } = await supabase.auth.getUser();

  const { error } = await supabase
    .from("contract_versions")
    .insert({
      contract_id: contractId,
      tenant_id: tenantId,
      version_number: nextVersion,
      title: contractData.title,
      status: contractData.status,
      metadata: contractData.metadata as any,
      signer_name: contractData.signer_name,
      signer_email: contractData.signer_email,
      expires_at: contractData.expires_at,
      changed_by: user?.id,
      change_summary: changeSummary
    } as any);

  if (error) throw error;
  return nextVersion;
}
